import { useEffect, useRef } from 'react';
import { Box } from '@mui/material';

const ITEM_SELECTOR = '[role="menuitem"]:not([disabled])';

// Menubar wrapper for a row of action buttons, following the WAI-ARIA menubar pattern
// (same behaviour as the Base UI Menubar shown in the MUI docs, built on MUI v5 parts):
// the bar is a single tab stop, Left/Right/Home/End move between items, and ArrowDown
// opens an item's dropdown. Children mark themselves as items with role="menuitem";
// dropdowns are ordinary MUI Menus/Popovers, which portal out of the bar.
export default function ActionMenuBar({ children, label, sx }) {
    const barRef = useRef(null);
    const activeIndexRef = useRef(0);

    const getItems = () => Array.from(barRef.current?.querySelectorAll(ITEM_SELECTOR) ?? []);

    const syncTabStops = () => {
        const items = getItems();
        activeIndexRef.current = Math.min(activeIndexRef.current, Math.max(items.length - 1, 0));
        items.forEach((item, i) => { item.tabIndex = i === activeIndexRef.current ? 0 : -1; });
    };

    // Items can be added, removed or disabled on any render, so re-sync every time.
    useEffect(syncTabStops);

    const handleFocus = (e) => {
        const index = getItems().indexOf(e.target);
        if (index !== -1) {
            activeIndexRef.current = index;
            syncTabStops();
        }
    };

    const handleKeyDown = (e) => {
        const items = getItems();
        const index = items.indexOf(e.target);
        if (index === -1) return;

        let next;
        if (e.key === 'ArrowRight') next = (index + 1) % items.length;
        else if (e.key === 'ArrowLeft') next = (index - 1 + items.length) % items.length;
        else if (e.key === 'Home') next = 0;
        else if (e.key === 'End') next = items.length - 1;
        else if (e.key === 'ArrowDown' && e.target.getAttribute('aria-haspopup')) {
            e.preventDefault();
            e.target.click();
            return;
        } else return;

        e.preventDefault();
        items[next].focus();
    };

    return (
        <Box
            ref={barRef}
            role="menubar"
            aria-label={label}
            onFocus={handleFocus}
            onKeyDown={handleKeyDown}
            sx={{
                // Same shadow as a default Paper, so the bar matches the cards below it.
                boxShadow: 1,
                borderRadius: 1,
                p: 0.25,
                display: 'flex',
                // Hug the items instead of spanning the page; still wraps on narrow screens.
                width: 'fit-content',
                maxWidth: '100%',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 0.25,
                backgroundColor: '#673ab7',
                '& .MuiButton-root': {
                    color: '#fff',
                    textTransform: 'none',
                    px: 1.25,
                    '&:hover, &.Mui-focusVisible': { backgroundColor: 'rgba(255, 255, 255, 0.12)' },
                    '&[aria-expanded="true"]': { backgroundColor: '#512da8' },
                    '&.Mui-disabled': { color: 'rgba(255, 255, 255, 0.5)' },
                },
                '& .MuiDivider-root': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                ...sx,
            }}
        >
            {children}
        </Box>
    );
}
